/**
 * 확정 처방 내역 목록.
 * admin-ruda-web `prescriptionDetails.js` 포팅.
 */
import {
  isPrescriptionExportEmptyError,
  isPrescriptionImageArchiveEmptyError,
} from '@/entities/prescription/api/prescription.api';
import {
  useExportFinalizedPrescriptionImages,
  useExportFinalizedPrescriptionsExcel,
  useFinalizedPrescriptionList,
  usePharmaCompaniesWithFinalizedPrescriptions,
} from '@/entities/prescription/hooks/usePrescriptions';
import { usePartnerCompanyOptions } from '@/entities/partner-company/hooks/usePartnerCompanies';
import { EmptyState } from '@/shared/components/EmptyState';
import { DataCard } from '@/shared/components/DataCard';
import { MonthPicker } from '@/shared/components/DatePicker';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { prescriptionHistoryDetailPath } from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import {
  formatBasicFeeType,
  formatCommissionRate,
  formatDetailFeeApplied,
  formatNumber,
  formatPlatformFeeRate,
  formatQuantity,
  formatYearMonth,
  getApprovalStatusBadgeVariant,
  getApprovalStatusLabel,
  getBasicFeeTypeBadgeVariant,
  getDefaultSettlementYm,
  getDetailFeeAppliedBadgeVariant,
  PRESCRIPTION_APPROVAL_FILTER_OPTIONS,
  type PrescriptionApprovalFilterValue,
} from '@/pages/prescriptions/prescriptionUtils';
import { openPresignedFile } from '@/shared/utils/openPresignedFile';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useUIStore } from '@/stores/uiStore';
import { useNavigate } from '@tanstack/react-router';
import { Download, FileText } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { FilterRow, FooterRow } from '@/shared/components/ui/row';
import {
  RadioGroup,
  RadioGroupButtonItem,
  RadioGroupItem,
} from '@/shared/components/ui/radio-group';

const ALL_PHARMA = 'all';
const ALL_PARENT_COMPANY = 'all';
const ZIP_PREPARING_TIP = '이미지 ZIP을 준비하는 중...';
const EXCEL_PREPARING_TIP = '엑셀 파일을 만드는 중...';
const BOTH_PREPARING_TIP = '엑셀과 이미지 ZIP을 준비하는 중...';

/** 다운로드 대상 — 전체는 엑셀·이미지 ZIP을 모두 받는다 */
type DownloadTarget = 'all' | 'images' | 'file';

const DOWNLOAD_TARGET_OPTIONS: { value: DownloadTarget; label: string }[] = [
  { value: 'all', label: '전체' },
  { value: 'images', label: '처방 이미지' },
  { value: 'file', label: '파일' },
];

const PRESCRIPTION_HISTORY_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  settlementYearMonth: getDefaultSettlementYm(),
  userEmail: '',
  clinicName: '',
  companyName: '',
  pharmaCompanyName: '',
  drugName: '',
  parentCompanyId: '',
  settlementCompanyId: '',
  approvalStatus: 'all' as PrescriptionApprovalFilterValue,
};

export const PrescriptionHistoryPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { values, setValues } = useListSearchParams(
    PRESCRIPTION_HISTORY_SEARCH_DEFAULTS,
  );

  const [settlementYmInput, setSettlementYmInput] = useState(
    values.settlementYearMonth,
  );
  const [emailInput, setEmailInput] = useState(values.userEmail);
  const [clinicInput, setClinicInput] = useState(values.clinicName);
  const [companyInput, setCompanyInput] = useState(values.companyName);
  const [pharmaInput, setPharmaInput] = useState(values.pharmaCompanyName);
  const [drugNameInput, setDrugNameInput] = useState(values.drugName);
  const { data: partnerCompanyOptions = [] } = usePartnerCompanyOptions();

  useEffect(() => {
    setSettlementYmInput(values.settlementYearMonth);
    setEmailInput(values.userEmail);
    setClinicInput(values.clinicName);
    setCompanyInput(values.companyName);
    setPharmaInput(values.pharmaCompanyName);
    setDrugNameInput(values.drugName);
  }, [
    values.settlementYearMonth,
    values.userEmail,
    values.clinicName,
    values.companyName,
    values.pharmaCompanyName,
    values.drugName,
  ]);

  const search = useMemo(
    () => ({
      settlementYearMonth: values.settlementYearMonth,
      userEmail: values.userEmail || undefined,
      clinicName: values.clinicName || undefined,
      companyName: values.companyName || undefined,
      pharmaCompanyName: values.pharmaCompanyName || undefined,
      drugName: values.drugName || undefined,
      parentCompanyId: values.parentCompanyId
        ? Number(values.parentCompanyId)
        : undefined,
      settlementCompanyId: values.settlementCompanyId
        ? Number(values.settlementCompanyId)
        : undefined,
      // 레거시: 'all'이 아니면 한글 승인 상태 문자열을 그대로 전달
      approvalStatus:
        values.approvalStatus && values.approvalStatus !== 'all'
          ? values.approvalStatus
          : undefined,
    }),
    [values],
  );

  const pageable = useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadYm, setDownloadYm] = useState(getDefaultSettlementYm());
  const [downloadPharma, setDownloadPharma] = useState<string>(ALL_PHARMA);
  const [downloadParentCompany, setDownloadParentCompany] =
    useState<string>(ALL_PARENT_COMPANY);
  const [downloadTarget, setDownloadTarget] = useState<DownloadTarget>('all');

  const excelExportMutation = useExportFinalizedPrescriptionsExcel();
  const imageArchiveMutation = useExportFinalizedPrescriptionImages();
  const setGlobalLoading = useUIStore((s) => s.setGlobalLoading);
  const isExporting = excelExportMutation.isPending;
  const isPreparingZip = imageArchiveMutation.isPending;
  const isDownloading = isExporting || isPreparingZip;
  /** 선택한 대상에 맞춰 로딩 문구를 고른다 */
  const preparingTip =
    downloadTarget === 'file'
      ? EXCEL_PREPARING_TIP
      : downloadTarget === 'images'
        ? ZIP_PREPARING_TIP
        : BOTH_PREPARING_TIP;

  useEffect(() => {
    setGlobalLoading(isDownloading, preparingTip);
    return () => setGlobalLoading(false);
  }, [isDownloading, preparingTip, setGlobalLoading]);
  const { data: pharmaCompanies = [], isLoading: pharmaLoading } =
    usePharmaCompaniesWithFinalizedPrescriptions(
      downloadOpen ? downloadYm.trim() || getDefaultSettlementYm() : null,
    );

  const { data, isLoading, isError, error } = useFinalizedPrescriptionList({
    search,
    pageable,
  });

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  const items = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      settlementYearMonth: settlementYmInput.trim() || getDefaultSettlementYm(),
      userEmail: emailInput.trim(),
      clinicName: clinicInput.trim(),
      companyName: companyInput.trim(),
      pharmaCompanyName: pharmaInput.trim(),
      drugName: drugNameInput.trim(),
      page: 1,
    });
  };

  /** 정산년월 MonthPicker 변경 시 즉시 목록 재조회 */
  const handleSettlementYmChange = (next: string) => {
    const ym = next.trim() || getDefaultSettlementYm();
    setSettlementYmInput(ym);
    setValues({
      settlementYearMonth: ym,
      page: 1,
    });
  };

  const openDownloadDialog = () => {
    const ym = settlementYmInput.trim() || getDefaultSettlementYm();
    setDownloadYm(ym);
    setDownloadPharma(ALL_PHARMA);
    setDownloadParentCompany(ALL_PARENT_COMPANY);
    setDownloadTarget('all');
    setDownloadOpen(true);
  };

  const handleDownload = async () => {
    const ym = downloadYm.trim() || getDefaultSettlementYm();
    const isAllPharma = downloadPharma === ALL_PHARMA;
    const pharmaCompanyId =
      isAllPharma ||
      !Number.isFinite(Number(downloadPharma)) ||
      Number(downloadPharma) <= 0
        ? undefined
        : Number(downloadPharma);
    const parentCompanyId =
      downloadParentCompany === ALL_PARENT_COMPANY ||
      !Number.isFinite(Number(downloadParentCompany))
        ? undefined
        : Number(downloadParentCompany);

    // 목록에 적용된 업체명 검색어를 엑셀·ZIP에 똑같이 넘겨 이미지명이 서로 맞게 한다
    const exportCompanyName = values.companyName || undefined;

    // 엑셀·이미지는 서로 독립이다. 한쪽이 비거나 실패해도 다른 쪽은 그대로 받는다.
    const wantsExcel = downloadTarget !== 'images';
    const wantsImages = downloadTarget !== 'file';
    let succeeded = 0;

    if (wantsExcel) {
      try {
        const excel = await excelExportMutation.mutateAsync({
          settlementYearMonth: ym,
          pharmaCompanyId,
          parentCompanyId,
          companyName: exportCompanyName,
        });
        // 엑셀·ZIP 모두 서버가 정한 파일명 그대로 저장된다.
        openPresignedFile(excel.downloadUrl);
        succeeded += 1;
      } catch (excelError) {
        if (isPrescriptionExportEmptyError(excelError)) {
          toast.info('추출할 확정 처방 내역이 없습니다.');
        } else {
          handleApiError(excelError, toast);
        }
      }
    }

    if (wantsImages) {
      try {
        const archive = await imageArchiveMutation.mutateAsync({
          settlementYearMonth: ym,
          pharmaCompanyId,
          parentCompanyId,
          companyName: exportCompanyName,
        });
        if (!archive.downloadUrl) {
          toast.info('다운로드할 이미지가 없습니다.');
        } else {
          openPresignedFile(archive.downloadUrl);
          succeeded += 1;
        }
      } catch (zipError) {
        if (isPrescriptionImageArchiveEmptyError(zipError)) {
          toast.info('다운로드할 이미지가 없습니다.');
        } else {
          handleApiError(zipError, toast);
        }
      }
    }

    if (succeeded > 0) {
      toast.success('확정 처방 내역을 다운로드했습니다.');
      setDownloadOpen(false);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FileText}
        title="확정 처방 내역"
        description="확정된 처방 내역 조회 및 다운로드"
        actions={
          <>
            <Button variant="outline" onClick={openDownloadDialog}>
              <Download className="mr-2 h-4 w-4" />
              처방내역 받기
            </Button>
          </>
        }
      />

      <Dialog
        open={downloadOpen}
        onOpenChange={(open) => {
          if (!open && isDownloading) return;
          setDownloadOpen(open);
        }}
      >
        <DialogContent
          showCloseButton={!isDownloading}
          onEscapeKeyDown={(event) => {
            if (isDownloading) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (isDownloading) event.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>확정 처방 내역 다운로드</DialogTitle>
            <DialogDescription>
              {isDownloading
                ? '서버에서 파일을 만들고 있습니다. 창을 닫지 마세요.'
                : '다운로드 대상과 정산 년월·상위업체·제약사를 선택해 받습니다. 이미지 수가 많으면 ZIP 생성이 오래 걸릴 수 있습니다. 제약사 전체와 상위업체를 함께 고르면 해당 업체의 전 제약사 내역을 받습니다.'}
            </DialogDescription>
          </DialogHeader>
          {isDownloading ? (
            <div
              className="py-8"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <LoadingSpinner tip={preparingTip} size="large" />
              {isPreparingZip ? (
                <p className="mt-2 text-center text-sm text-muted-foreground">
                  이미지 수에 따라 수 분이 걸릴 수 있습니다.
                </p>
              ) : null}
            </div>
          ) : (
            <div className="grid gap-4 py-2">
              {values.companyName ? (
                <p className="rounded-8 bg-ruda-bg-tertiary px-3 py-2 text-sm text-foreground">
                  목록의 업체명 검색어{' '}
                  <span className="font-medium">「{values.companyName}」</span>
                  가 엑셀·이미지에 함께 적용됩니다.
                </p>
              ) : null}
              <div className="space-y-2">
                <Label>다운로드 대상</Label>
                <RadioGroup
                  value={downloadTarget}
                  onValueChange={(v) => setDownloadTarget(v as DownloadTarget)}
                  className="grid-flow-col auto-cols-fr gap-2"
                  aria-label="다운로드 대상"
                >
                  {DOWNLOAD_TARGET_OPTIONS.map((opt) => (
                    <RadioGroupButtonItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </RadioGroupButtonItem>
                  ))}
                </RadioGroup>
              </div>
              <div className="space-y-2">
                <Label htmlFor="download-ym">정산 년월 (YYYYMM)</Label>
                <MonthPicker
                  id="download-ym"
                  value={downloadYm}
                  onChange={(v) => {
                    setDownloadYm(v);
                    setDownloadPharma(ALL_PHARMA);
                    setDownloadParentCompany(ALL_PARENT_COMPANY);
                  }}
                  placeholder="년월 선택"
                />
              </div>
              <div className="space-y-2">
                <Label>상위업체</Label>
                <Select
                  value={downloadParentCompany}
                  onValueChange={setDownloadParentCompany}
                >
                  <SelectTrigger aria-label="다운로드 상위업체">
                    <SelectValue placeholder="전체" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_PARENT_COMPANY}>전체</SelectItem>
                    {partnerCompanyOptions.map((option) => (
                      <SelectItem
                        key={option.partnerCompanyId}
                        value={String(option.partnerCompanyId)}
                      >
                        {option.companyName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>제약사</Label>
                <Select
                  value={downloadPharma}
                  onValueChange={setDownloadPharma}
                  disabled={pharmaLoading}
                >
                  <SelectTrigger aria-label="다운로드 제약사">
                    <SelectValue placeholder="전체" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_PHARMA}>전체</SelectItem>
                    {pharmaCompanies.map((p) => (
                      <SelectItem
                        key={p.pharmaCompanyId}
                        value={String(p.pharmaCompanyId)}
                      >
                        {p.pharmaCompanyName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <FooterRow>
            <Button
              variant="outline"
              onClick={() => setDownloadOpen(false)}
              disabled={isDownloading}
            >
              취소
            </Button>
            <Button
              onClick={handleDownload}
              disabled={isDownloading || pharmaLoading}
            >
              {isDownloading ? '처리 중...' : '다운로드'}
            </Button>
          </FooterRow>
        </DialogContent>
      </Dialog>

      <FilterCard>
        <form className="space-y-4" onSubmit={handleSearch}>
          <FilterRow as="div" gap={12}>
            <Field label="정산 년월" htmlFor="hist-ym">
              <MonthPicker
                id="hist-ym"
                placeholder="년월 선택"
                value={settlementYmInput}
                onChange={handleSettlementYmChange}
                className="w-44"
              />
            </Field>
          </FilterRow>

          <FilterRow as="div" gap={12}>
            <Field label="사용자 이메일" htmlFor="hist-email">
              <Input
                id="hist-email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                className="w-40"
              />
            </Field>
            <Field label="병의원명" htmlFor="hist-clinic">
              <Input
                id="hist-clinic"
                value={clinicInput}
                onChange={(e) => setClinicInput(e.target.value)}
                className="w-36"
              />
            </Field>
            <Field label="업체명" htmlFor="hist-company">
              <Input
                id="hist-company"
                value={companyInput}
                onChange={(e) => setCompanyInput(e.target.value)}
                className="w-36"
              />
            </Field>
            <Field label="제약사명" htmlFor="hist-pharma">
              <Input
                id="hist-pharma"
                value={pharmaInput}
                onChange={(e) => setPharmaInput(e.target.value)}
                className="w-36"
              />
            </Field>
            <Field label="의약품명" htmlFor="hist-drug">
              <Input
                id="hist-drug"
                value={drugNameInput}
                onChange={(e) => setDrugNameInput(e.target.value)}
                className="w-36"
              />
            </Field>
            <Field label="상위업체">
              <Select
                value={values.parentCompanyId || 'all'}
                onValueChange={(value) =>
                  setValues({
                    parentCompanyId: value === 'all' ? '' : value,
                    page: 1,
                  })
                }
              >
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="전체" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">전체</SelectItem>
                  {partnerCompanyOptions.map((option) => (
                    <SelectItem
                      key={option.partnerCompanyId}
                      value={String(option.partnerCompanyId)}
                    >
                      {option.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="정산업체">
              <Select
                value={values.settlementCompanyId || 'all'}
                onValueChange={(value) =>
                  setValues({
                    settlementCompanyId: value === 'all' ? '' : value,
                    page: 1,
                  })
                }
              >
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="전체" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">전체</SelectItem>
                  {partnerCompanyOptions.map((option) => (
                    <SelectItem
                      key={option.partnerCompanyId}
                      value={String(option.partnerCompanyId)}
                    >
                      {option.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Button type="submit" className="ruda-button">
              검색
            </Button>
          </FilterRow>

          <div className="space-y-2">
            <Label>처방내역 승인 상태</Label>
            <RadioGroup
              value={values.approvalStatus || 'all'}
              onValueChange={(v) =>
                setValues({
                  approvalStatus: v as PrescriptionApprovalFilterValue,
                  page: 1,
                })
              }
              className="flex flex-wrap gap-4"
            >
              {PRESCRIPTION_APPROVAL_FILTER_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  htmlFor={`hist-approval-${opt.value}`}
                  className="flex cursor-pointer items-center gap-2 text-sm"
                >
                  <RadioGroupItem
                    value={opt.value}
                    id={`hist-approval-${opt.value}`}
                  />
                  {opt.label}
                </label>
              ))}
            </RadioGroup>
          </div>
        </form>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <div className="py-16">
            <LoadingSpinner tip="처방 내역을 불러오는 중..." />
          </div>
        ) : items.length === 0 ? (
          <EmptyState description="검색 조건에 맞는 처방 내역이 없습니다." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>사용자 이메일</TableHead>
                  <TableHead>업체명</TableHead>
                  <TableHead>처방 년 월</TableHead>
                  <TableHead>정산 년월</TableHead>
                  <TableHead>병의원명</TableHead>
                  <TableHead>제약사명</TableHead>
                  <TableHead>상위업체</TableHead>
                  <TableHead>정산업체</TableHead>
                  <TableHead>의약품 코드</TableHead>
                  <TableHead>의약품명</TableHead>
                  <TableHead>약가</TableHead>
                  <TableHead>원내 사용량</TableHead>
                  <TableHead>원외 처방량</TableHead>
                  <TableHead>원외 처방금액</TableHead>
                  <TableHead>기본 수수료율</TableHead>
                  <TableHead>상세 수수료율</TableHead>
                  <TableHead>표준수당율</TableHead>
                  <TableHead>플랫폼 수수료율</TableHead>
                  <TableHead>정산 수수료율</TableHead>
                  <TableHead>정산 예상 금액</TableHead>
                  <TableHead>처방내역 승인 상태</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.prescriptionId}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      navigate({
                        to: prescriptionHistoryDetailPath(item.prescriptionId),
                      } as never)
                    }
                  >
                    <TableCell>{item.email}</TableCell>
                    <TableCell>{item.companyName || item.ceoName}</TableCell>
                    <TableCell>
                      {formatYearMonth(item.prescriptionYearMonth)}
                    </TableCell>
                    <TableCell>
                      {formatYearMonth(item.settlementYearMonth)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {item.clinicName}
                    </TableCell>
                    <TableCell>{item.pharmaCompanyName}</TableCell>
                    <TableCell>{item.parentCompanyName || '-'}</TableCell>
                    <TableCell>{item.settlementCompanyName || '-'}</TableCell>
                    <TableCell>{item.drugCode || '-'}</TableCell>
                    <TableCell>{item.drugName}</TableCell>
                    <TableCell>{formatNumber(item.price)}</TableCell>
                    <TableCell>
                      {formatQuantity(item.inHospitalQuantity)}
                    </TableCell>
                    <TableCell>
                      {formatQuantity(item.outHospitalQuantity)}
                    </TableCell>
                    <TableCell>
                      {formatNumber(item.outHospitalAmount)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getBasicFeeTypeBadgeVariant(item.basicFeeType)}
                      >
                        {formatBasicFeeType(item.basicFeeType)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getDetailFeeAppliedBadgeVariant(
                          item.isDetailFeeApplied,
                        )}
                      >
                        {formatDetailFeeApplied(item.isDetailFeeApplied)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {formatCommissionRate(item.standardCommissionRate)}
                    </TableCell>
                    <TableCell>
                      {formatPlatformFeeRate(item.platformFeeRate)}
                    </TableCell>
                    <TableCell>
                      {formatCommissionRate(item.commissionRate)}
                    </TableCell>
                    <TableCell>
                      {item.estimatedAmount != null
                        ? Math.floor(item.estimatedAmount).toLocaleString(
                            'ko-KR',
                          )
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getApprovalStatusBadgeVariant(
                          item.approvalStatus,
                        )}
                      >
                        {getApprovalStatusLabel(item.approvalStatus)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>
    </div>
  );
};
